import React from "react";
import SvgMathLabel, { SvgRightAngle } from "./SvgMathLabel";

/**
 * Trapez prostokątny:
 *  a — GÓRNA podstawa, b — DOLNA podstawa, h — wysokość (lewy bok prostopadły).
 *
 * Kolory:
 *  - krawędzie + etykiety a,b,h: currentColor (dziedziczy z motywu)
 *  - d₁: fiolet #8b5cf6, d₂: zieleń #10b981
 *  - highlightLeg: domyślnie fiolet #8b5cf6 (nakładka na prawym skośnym ramieniu)
 *  - aux (w wyjaśnieniu): h przy prawym boku i odcinek (b−a) na dole
 */
export default function RightTrapezoidDiagram({
  a,
  b,
  h,
  showDiagonals = false,
  showValues = true,
  topLabel = null,
  bottomLabel = null,
  heightLabel = null,
  showRightAngles = false,
  highlightLeg = false,
  legLabel = "",
  legColor = "#8b5cf6",
  // NOWE: pomocniczy trójkąt z h i (b−a) – używaj w wyjaśnieniu
  showAuxRightTriangle = false,
  size = 420,
  padTop = 40,
  padBottom = 36,
  padX = 32,
  strokeW = 2.8,
  className,
  style,
}) {
  const A = Math.max(1, Number(a) || 1); // top
  const B = Math.max(1, Number(b) || 1); // bottom
  const H = Math.max(1, Number(h) || 1);

  const W = size;
  const SvgH = Math.round(size * 0.50);

  const innerW = W - 2 * padX;
  const innerH = SvgH - padTop - padBottom;

  // skala względem dołu i wysokości (pewniejsze dopasowanie)
  const unit = Math.min(innerW / B, innerH / H);

  const leftX = padX;
  const baseY = padTop + innerH;
  const topY  = baseY - H * unit;

  const rightX    = leftX + B * unit; // dół
  const topLeftX  = leftX;            // góra
  const topRightX = leftX + A * unit; // góra

  const COLORS = {
    base: "currentColor",
    d1: "#8b5cf6",
    d2: "#10b981",
    auxH: "#f59e0b",   // pomocnicze h
    auxX: "#a78bfa",   // odcinek (b−a)
  };

  const labelA = topLabel || (showValues ? `a=${a}` : "a");
  const labelB = bottomLabel || (showValues ? `b=${b}` : "b");
  const labelH = heightLabel || (showValues ? `h=${h}` : "h");

  return (
    <svg
      viewBox={`0 0 ${W} ${SvgH}`}
      preserveAspectRatio="xMidYMid meet"
      className={className}
      style={{
        display: "block",
        margin: "0 auto",
        width: "100%",
        height: "auto",
        maxWidth: W,
        overflow: "visible",
        ...style,
      }}
    >
      {/* krawędzie */}
      <line x1={leftX} y1={baseY} x2={rightX} y2={baseY}
            stroke={COLORS.base} strokeWidth={strokeW} strokeLinecap="round" />
      <line x1={leftX} y1={baseY} x2={leftX} y2={topY}
            stroke={COLORS.base} strokeWidth={strokeW} strokeLinecap="round" />
      <line x1={rightX} y1={baseY} x2={topRightX} y2={topY}
            stroke={COLORS.base} strokeWidth={strokeW} strokeLinecap="round" />
      <line x1={topLeftX} y1={topY} x2={topRightX} y2={topY}
            stroke={COLORS.base} strokeWidth={strokeW} strokeLinecap="round" />

      {/* lewa wysoko?? h */}
      <line x1={leftX} y1={topY} x2={leftX} y2={baseY}
            stroke={COLORS.base} strokeWidth={strokeW} strokeLinecap="round" />
      {showRightAngles && (
        <>
          <SvgRightAngle V={{ x: leftX, y: topY }} u={{ x: 1, y: 0 }} v={{ x: 0, y: 1 }} radius={14} color={COLORS.base} strokeWidth={1.8} />
          <SvgRightAngle V={{ x: leftX, y: baseY }} u={{ x: 0, y: -1 }} v={{ x: 1, y: 0 }} radius={14} color={COLORS.base} strokeWidth={1.8} />
        </>
      )}
      <SvgMathLabel x={leftX - 10} y={(topY + baseY) / 2} math={labelH} width={70} height={24} fontSize={15} align="right" color={COLORS.base} />

      {/* przekątne (opcjonalne) */}
      {showDiagonals && (
        <>
          <line x1={topLeftX} y1={topY} x2={rightX} y2={baseY}
                stroke={COLORS.d1} strokeWidth={strokeW} strokeLinecap="round" />
          <SvgMathLabel x={(topLeftX + rightX) / 2 + 12} y={(topY + baseY) / 2 - 8} math="d_1" width={36} height={22} fontSize={14} color={COLORS.d1} />

          <line x1={leftX} y1={baseY} x2={topRightX} y2={topY}
                stroke={COLORS.d2} strokeWidth={strokeW} strokeLinecap="round" />
          <SvgMathLabel x={(leftX + topRightX) / 2} y={(baseY + topY) / 2 + 18} math="d_2" width={36} height={22} fontSize={14} color={COLORS.d2} />
        </>
      )}

      {/* podświetlenie ramienia (opcjonalne) */}
      {highlightLeg && (
        <>
          <line x1={rightX} y1={baseY} x2={topRightX} y2={topY}
                stroke={legColor} strokeWidth={strokeW + 0.8} strokeLinecap="round" />
          {legLabel && (
            <SvgMathLabel x={(rightX + topRightX) / 2 + 14} y={(baseY + topY) / 2} math={legLabel} width={40} height={22} fontSize={14} color={legColor} />
          )}
        </>
      )}

      {/* NOWE: pomocniczy trójkąt przy prawym boku – h i (b−a) */}
      {showAuxRightTriangle && (
        <>
          {/* pionowy rzut: h */}
          <line x1={topRightX} y1={topY} x2={topRightX} y2={baseY}
                stroke={COLORS.auxH} strokeWidth={strokeW} strokeLinecap="round" />
          <SvgMathLabel x={topRightX + 20} y={(topY + baseY) / 2} math="h" width={30} height={22} fontSize={14} color={COLORS.auxH} />

          {/* odcinek (b−a) na dole */}
          <line x1={topRightX} y1={baseY} x2={rightX} y2={baseY}
                stroke={COLORS.auxX} strokeWidth={strokeW + 0.8} strokeLinecap="round" />
          <SvgMathLabel x={(topRightX + rightX) / 2} y={baseY + 20} math="b-a" width={50} height={22} fontSize={14} color={COLORS.auxX} />
        </>
      )}

      {/* podpisy podstaw */}
      <SvgMathLabel x={(topLeftX + topRightX) / 2} y={topY - 12} math={labelA} width={80} height={24} fontSize={15} color={COLORS.base} />
      <SvgMathLabel x={(leftX + rightX) / 2} y={baseY + 22} math={labelB} width={80} height={24} fontSize={15} color={COLORS.base} />
    </svg>
  );
}
